import { ShareStatus } from './graphql/generated';
import { TransactionEntryTag } from './react/cashflow';
import { capitalize } from './util/strings';

export const TOAST_DURATIONS = {
    short: 3_000, // 3 seconds
    long: 10_000, // 10 seconds
};

type ToastParams =
    | string
    | {
          title?: string;
          description?: string;
          duration?: number; // remove this?
      }
    | ((...args: any[]) => string)
    | ((...args: any[]) => {
          title?: string;
          description?: string;
          duration?: number; // remove this?
      });

export const TOASTS = {
    acceptPartnerInviteExpiredError: {
        title: 'Invitation expired',
        description:
            'To keep both of your accounts secure, invitations expire if they’ve been unused for a while. We’re sorry for the inconvenience! Please ask your partner to send you a new invitation to join Plenty, and we’ll get you set up in just a few clicks.',
        duration: 10000,
    },

    acceptPartnerInviteUnknownError:
        'An unexpected error occurred while accepting the invitation.',

    addPaymentMethodSuccess:
        'Payment method successfully added. Thank you for becoming a Plenty member!',

    applyPromotionCodeError:
        'An error occurred while applying your promotion code.',

    attachmentTooLarge: 'Attachments must be less than 50 MB',

    budgetingOnlyInOursView:
        'Budgeting is currently only supported in the “What’s Ours” view. We’ve switched your view so you can use budgeting.',

    categoryNameAlreadyExists:
        'A category with the same name already exists. Please choose a different name.',

    checkYourEmail: ({ email }: { email: string }) => ({
        title: 'Check your email',
        description: `Your sign in link was sent to ${email}.`,
        duration: 20_000,
    }),

    comingSoonFeedbackSuccess: {
        description: 'Thanks for your feedback!',
        duration: TOAST_DURATIONS.short,
    },

    confirmConsentError:
        'There was an unexpected error confirming your consent.',

    copySharingLinkError:
        'An unexpected error occurred while copying the sharing link',

    copySharingLinkSuccess: 'Sharing link copied to clipboard!',

    createOrUpdateCategoryUnexpectedError:
        'An unexpected error occurred while creating the category. Please try again.',

    createOrUpdateSubcategoryUnexpectedError:
        'An unexpected error occurred while creating the subcategory. Please try again.',

    createOrUpdateTagError: ({
        existingTag,
    }: {
        existingTag: TransactionEntryTag | undefined;
    }) =>
        `An unexpected error occurred while ${existingTag ? 'updating' : 'creating'} the tag. Please try again.`,

    createOrUpdateTagSuccess: ({
        tagName,
        existingTag,
    }: {
        tagName: string;
        existingTag: TransactionEntryTag | undefined;
    }) =>
        `"${tagName}" tag ${existingTag ? 'updated' : 'created'} successfully`,

    createSubcategorySuccess: 'Subcategory created successfully',

    createTransferUnknownError:
        'We hit a snag creating your transfer. Please refresh the page and try again.',

    createTransactionError: {
        title: 'Could not initiate your deposit',
        description:
            'There was a problem initiating your deposit. Please try again, or contact support if the issue continues.',
    },

    createTransactionSuccess: 'Transfer initiated successfully',

    createTransactionScheduleError: {
        title: 'Could not schedule your recurring deposit',
        description:
            'There was a problem scheduling your deposit. Please try again, or contact support if the issue continues.',
    },

    createTransactionScheduleSuccess:
        'Recurring deposit scheduled successfully',

    createUserFeedbackError:
        'There was an error submitting your feedback. Please reach out to us at members@withplenty.com.',

    createUserFeedbackUploadImageError:
        'There was an error uploading your attachment. Please reach out to us at members@withplenty.com.',

    declinePartnershipInviteSuccess: {
        title: 'Invitation declined',
        description:
            'If you think you were invited fraudulently, please reach out to us immediately at priority@withplenty.com',
        duration: 20_000,
    },

    deleteCategoryError:
        'An unexpected error occurred while deleting the category. Please try again.',

    deleteCategorySuccess: 'Category deleted successfully',

    deleteGoalZeroBalanceSuccess: 'Goal successfully deleted',

    deleteGoalHasBalanceSuccess: ({
        feName,
    }: {
        feName: string | null | undefined;
    }) =>
        `Goal successfully deleted. We have initiated a transfer of funds to ${feName ?? 'your account'}. The transfer will be complete in 3-5 business days.`,

    deleteRuleSuccess: 'Rule successfully deleted',

    deleteSubcategoryError:
        'An unexpected error occurred while deleting the subcategory. Please try again.',

    deleteSubcategorySuccess: 'Subcategory deleted successfully',

    deleteTagError: 'An unexpected error occurred while deleting the tag',

    deleteTagSuccess: 'Tag deleted successfully',

    deleteTransactionScheduleError: {
        title: 'Failed to stop recurring deposit',
        description:
            'There was a problem stopping your recurring deposit. Please try again, or contact support if the issue continues.',
    },

    deleteTransactionScheduleSuccess: 'Recurring deposit stopped',

    downloadDocumentError:
        'There was a problem downloading the document. Please try again, or reach out to support.',

    emailNotFound: 'The email address you entered does not exist in our system',

    findMatchingTransactionsError:
        'There was an unexpected error fetching similar transactions.',

    invalidZipCode: {
        title: 'That ZIP code doesn’t seem valid. Could you confirm and re-enter it?',
        duration: TOAST_DURATIONS.short,
    },

    invertAmountsSuccess: 'Transaction signs were updated successfully',

    leaveSplitGoalHasBalanceSuccess: ({
        feName,
    }: {
        feName: string | null | undefined;
    }) =>
        `You have successfully left the split goal. It has been changed to a private goal for your partner. We have initiated a transfer of funds to ${feName ?? 'your account'}. The transfer will be complete in 3-5 business days.`,

    leaveSplitGoalZeroBalanceSuccess:
        'You have successfully left the split goal. It has been changed to a private goal for your partner and is not visible to you unless shared by your partner.',

    manualAddAccountError: {
        title: 'We hit a snag',
        description:
            'There was a problem adding your account. Our team is on it! Meanwhile, please try again, or contact support if the issue continues.',
    },

    partnerModeEnabled: 'Partner mode enabled',

    partnerInviteSuccess: {
        title: 'Invite email sent successfully!',
        description:
            'You can see the status of your invitation in Settings > Sharing',
    },

    recommendedRiskScoreUnlocked: {
        title: 'Recommended risk score unlocked',
        description:
            'Now that you’ve completed ID verification, Plenty can recommend a risk score based on your goal target date.',
    },

    referralCodeCopied: {
        description: 'Referral code copied to clipboard!',
        duration: TOAST_DURATIONS.short,
    },

    referralLinkCopied: {
        description: 'Referral link copied to clipboard!',
        duration: TOAST_DURATIONS.short,
    },

    relinkSuccess: 'Institution successfully reconnected',

    requestCountryAvailabilitySuccess:
        "Thank you for your input! We're excited to explore new possibilities",

    resendPartnershipInviteError:
        'An unexpected error occurred while resending the invitation.',

    signInError: ({ description }: { description: string | undefined }) => ({
        title: 'We hit a snag',
        description:
            description ||
            'There was a problem signing you in. Please try again, or contact support if the issue continues.',
    }),

    soloModeEnabled: 'Solo mode enabled',

    splitTransactionSuccess: 'Transaction split successfully',
    splitTransactionGenericError:
        'There was an unexpected error splitting the transaction',
    splitTransactionAmountError:
        'Split amounts must add up to the original transaction amount',

    subcategoryNameAlreadyExists:
        'A subcategory with the same name already exists in the selected group. Please choose a different name.',

    tagNameAlreadyExists:
        'A tag with the same name already exists. Please choose a different name.',

    updateTransactionSuccess: 'Transaction updated successfully!',

    updateBudgetError:
        'An unexpected error occurred while updating your budget. Please try again.',

    updateBudgetSuccess: 'Budget updated successfully!',

    updateCategorySuccess: 'Category updated successfully',

    updateFinancialEntityShareStatusSuccess: ({
        shareStatus,
    }: {
        shareStatus: ShareStatus;
    }) => ({
        description: `Account visibility changed to ${capitalize(shareStatus)}`,
        duration: TOAST_DURATIONS.short,
    }),

    updateFinancialEntitySuccess: ({ feName }: { feName: string }) => ({
        description: feName + ' updated successfully',
        duration: TOAST_DURATIONS.short,
    }),

    updateGoalError: {
        title: 'We hit a snag',
        description:
            'There was a problem updating your goal. Our team is on it! Meanwhile, please try again, or contact support if the issue continues.',
    },

    updateRulePriorityError:
        'There was an error updating the priority of the rule.',

    updateSubcategorySuccess: 'Subcategory updated successfully',

    updateTransactionEntryDateError:
        'An unexpected error occurred while updating the transaction date. Please try again.',

    updateTransactionEntryError:
        'An unexpected error occurred while updating the transaction.',

    updateTransactionScheduleError: {
        title: 'Could not update your recurring deposit',
        description:
            'There was a problem updating your deposit. Please try again, or contact support if the issue continues.',
    },

    updateUserProfileError: 'There was an error updating your information',

    updateUserProfileSuccess: 'Your profile was successfully updated',

    unexpectedError: 'An unexpected error occurred. Please try again.',

    unexpectedErrorTryAgainLater:
        'An unexpected error occurred. Please try again later.',

    unexpectedErrorPleaseReachOut:
        'An unexpected error occurred. Please try again or reach out to us at members@withplenty.com.',
} satisfies { [key: string]: ToastParams };
